/* ============================================================
   DatabasePage — type='database' 페이지의 표/보드/캘린더 뷰
   라우트 제안: /db/:pageId
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import RelationCell from './RelationCell';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell as RechartsCell, LineChart, Line, ResponsiveContainer,
} from 'recharts';
import { supabase } from '../../supabaseClient';
import { useDatabase } from './useDatabase';
import BoardView from './BoardView';
import CalendarView from './CalendarView';
import GalleryView from './GalleryView';
import ListView from './ListView';
import TimelineView from './TimelineView';
import { FilterControl, SortControl, SearchControl } from './ViewToolbar';
import { applyPipeline } from './viewPipeline';
import { useRelationContext } from './useRelationContext';
import PersonCell from './PersonCell';
import RelationEditor from './RelationEditor';
import { parseFormula } from './formulaEngine';
import {
  computeFormula, computeRollup, formatVal, personNames,
  makeGetValue, ROLLUP_AGGS, UUID_RE,
} from './computeValue';
import './database.css';

/* select 옵션 color → 목업 태그 클래스 매핑 */
const COLOR_CLASS = {
  gray: 'tg', default: 'tg',
  blue: 'tbl', green: 'tgr', yellow: 'tyl',
  orange: 'tor', purple: 'tpu', red: 'trd', pink: 'trd', brown: 'tor',
};

/* select 옵션 color → 실제 색상값 (차트용) */
const COLOR_HEX = {
  gray: '#9b9a97', default: '#9b9a97',
  blue: '#2383e2', green: '#2b9348', yellow: '#e0a020',
  orange: '#e07b39', purple: '#8b5cf6', red: '#e0533d',
  pink: '#e0517f', brown: '#a87143',
};
/* 옵션 색이 없을 때 순환 사용할 기본 팔레트 */
const CHART_PALETTE = ['#2383e2', '#8b5cf6', '#2b9348', '#e07b39', '#16a085', '#e0533d', '#e0a020', '#e0517f'];

/* 옵션 색 지정 UI 용 팔레트 */
const OPTION_COLORS = [
  { name: 'gray', label: '회색' },
  { name: 'blue', label: '파랑' },
  { name: 'green', label: '초록' },
  { name: 'yellow', label: '노랑' },
  { name: 'orange', label: '주황' },
  { name: 'purple', label: '보라' },
  { name: 'red', label: '빨강' },
];

/* 선택 가능한 컬럼 타입 (title 제외) */
const COLUMN_TYPES = [
  { type: 'text', label: '텍스트', icon: 'Aa' },
  { type: 'select', label: '선택', icon: '◉' },
  { type: 'date', label: '날짜', icon: '📅' },
  { type: 'person', label: '사람', icon: '👤' },
  { type: 'checkbox', label: '체크박스', icon: '☑' },
  { type: 'number', label: '숫자', icon: '#' },
  { type: 'url', label: 'URL', icon: '🔗' },
  { type: 'relation', label: '관계', icon: '↗' },
  { type: 'formula', label: '수식', icon: 'ƒ' },
  { type: 'rollup', label: '롤업', icon: 'Σ' },
];

/* 계산형 속성 (직접 입력값 없음 — 폼/자동화/차트 그룹에서 제외) */
const COMPUTED_TYPES = ['formula', 'rollup'];

function optClass(col, value) {
  const opt = (col.options || []).find((o) => o.name === value);
  return COLOR_CLASS[opt?.color] || 'tg';
}

const VIEW_LABEL = {
  table: '표', board: '보드', calendar: '캘린더', gallery: '갤러리',
  list: '리스트', timeline: '타임라인', chart: '차트', form: '폼',
};

/* 외부 클릭 감지 훅 */
function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

/* ====================== 셀 렌더링 ====================== */
/* formula 셀 — 렌더 시 계산, 오류는 "#오류" + 툴팁 */
function FormulaCell({ col, row, columns, dbx }) {
  if (!String(col.formula || '').trim()) {
    return <div className="cell"><span className="fx-empty">수식 없음</span></div>;
  }
  const res = computeFormula(col, row, columns, dbx);
  if (res.error) {
    return <div className="cell"><span className="fx-err" title={res.error}>#오류</span></div>;
  }
  return <div className="cell"><span className="fx-val">{formatVal(res.value)}</span></div>;
}

/* rollup 셀 — relation 대상 행들을 집계 (대상 DB 는 batch fetch 캐시) */
function RollupCell({ col, row, columns, dbx }) {
  const res = computeRollup(col, row, columns, dbx);
  if (res.pending) {
    return <div className="cell"><span className="fx-empty">…</span></div>;
  }
  if (res.error) {
    return <div className="cell"><span className="fx-err" title={res.error}>#오류</span></div>;
  }
  return <div className="cell"><span className="fx-val">{formatVal(res.value)}</span></div>;
}

function Cell({ col, row, columns, dbx, onChange, onUpdateColumnOptions }) {
  const value = col.key === 'title' ? row.title : (row.properties?.[col.key] ?? '');

  if (col.type === 'title') {
    return (
      <div className="cell title">
        <span className="row-emoji">{row.icon || '📄'}</span>
        <input
          className="cell-input"
          value={value}
          placeholder="제목 없음"
          onChange={(e) => onChange(col.key, e.target.value)}
        />
      </div>
    );
  }

  if (col.type === 'select') {
    return (
      <SelectCell
        col={col}
        value={value}
        onChange={onChange}
        onUpdateColumnOptions={onUpdateColumnOptions}
      />
    );
  }

  // person: 조직 멤버 선택기 (저장 = user id 배열, 레거시 평문 하위호환)
  if (col.type === 'person') {
    return (
      <PersonCell
        col={col}
        value={value}
        onChange={onChange}
        members={dbx.members}
        peopleById={dbx.peopleById}
      />
    );
  }

  // relation: 대상 DB 행 검색 팝오버로 다중 연결/해제 (칩 = /page 링크 유지)
  if (col.type === 'relation') {
    return <RelationEditor col={col} value={value} onChange={onChange} dbx={dbx} />;
  }

  if (col.type === 'formula') {
    return <FormulaCell col={col} row={row} columns={columns} dbx={dbx} />;
  }

  if (col.type === 'rollup') {
    return <RollupCell col={col} row={row} columns={columns} dbx={dbx} />;
  }

  // 레거시: relation 타입이 아닌 컬럼에 배열 값(노션 임포트 산출) → 읽기 전용 칩
  if (Array.isArray(value)) {
    return <RelationCell value={value} titleById={dbx.titleById} />;
  }

  if (col.type === 'date') {
    return (
      <div className="cell">
        <input
          className="cell-date"
          type="date"
          value={value || ''}
          onChange={(e) => onChange(col.key, e.target.value)}
        />
      </div>
    );
  }

  if (col.type === 'checkbox') {
    return (
      <div className="cell cell-center">
        <input
          type="checkbox"
          className="cell-check"
          checked={value === true || value === 'true'}
          onChange={(e) => onChange(col.key, e.target.checked)}
        />
      </div>
    );
  }

  if (col.type === 'number') {
    return (
      <div className="cell">
        <input
          className="cell-input cell-num"
          type="number"
          value={value === 0 || value ? value : ''}
          placeholder="비어 있음"
          onChange={(e) => onChange(col.key, e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
    );
  }

  if (col.type === 'url') {
    return (
      <div className="cell cell-url">
        <input
          className="cell-input"
          type="url"
          value={value}
          placeholder="링크 추가"
          onChange={(e) => onChange(col.key, e.target.value)}
        />
        {value && (
          <a href={value} target="_blank" rel="noreferrer" className="url-open" title="열기">↗</a>
        )}
      </div>
    );
  }

  // 기본: 텍스트
  return (
    <div className="cell">
      <input className="cell-input" value={value} onChange={(e) => onChange(col.key, e.target.value)} />
    </div>
  );
}

/* select 셀 — 드롭다운에서 선택 + 새 옵션 추가 */
function SelectCell({ col, value, onChange, onUpdateColumnOptions }) {
  const [open, setOpen] = useState(false);
  const [newOpt, setNewOpt] = useState('');
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const options = col.options || [];
  const cls = value ? optClass(col, value) : '';

  const pick = (name) => {
    onChange(col.key, name);
    setOpen(false);
  };

  const addOption = async () => {
    const name = newOpt.trim();
    if (!name) return;
    if (!options.some((o) => o.name === name)) {
      const palette = OPTION_COLORS[options.length % OPTION_COLORS.length].name;
      await onUpdateColumnOptions(col.key, [...options, { name, color: palette }]);
    }
    onChange(col.key, name);
    setNewOpt('');
    setOpen(false);
  };

  return (
    <div className="cell" ref={ref}>
      <span
        className={`tag-trigger ${value ? 'tag ' + cls : 'muted'}`}
        onClick={() => setOpen((o) => !o)}
      >
        {value || '비어 있음'}
      </span>
      {open && (
        <div className="opt-pop">
          <div className="opt-list">
            <div className="opt-item" onClick={() => pick('')}>
              <span className="muted">비어 있음</span>
            </div>
            {options.map((o) => (
              <div key={o.name} className="opt-item" onClick={() => pick(o.name)}>
                <span className={`tag ${COLOR_CLASS[o.color] || 'tg'}`}>{o.name}</span>
              </div>
            ))}
          </div>
          <div className="opt-add">
            <input
              value={newOpt}
              placeholder="옵션 추가…"
              onChange={(e) => setNewOpt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addOption(); }}
            />
            <button onClick={addOption}>추가</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== 컬럼 헤더 메뉴 ====================== */
function ColumnHeader({ col, columns, renameColumn, changeColumnType, deleteColumn, updateColumnOptions, updateColumnMeta, dbx }) {
  const [open, setOpen] = useState(false);
  const [editName, setEditName] = useState(col.name);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  useEffect(() => { setEditName(col.name); }, [col.name]);

  const isTitle = col.type === 'title';

  const commitName = () => {
    const n = editName.trim();
    if (n && n !== col.name) renameColumn(col.key, n);
  };

  return (
    <th>
      <div className="th-inner" ref={ref}>
        <button className="th-btn" onClick={() => !isTitle && setOpen((o) => !o)} disabled={isTitle}>
          <span className="ti">{iconFor(col.type)}</span>
          <span className="th-name">{col.name}</span>
        </button>
        {open && !isTitle && (
          <div className="col-menu">
            <div className="cm-section">
              <input
                className="cm-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => { if (e.key === 'Enter') { commitName(); setOpen(false); } }}
              />
            </div>
            <div className="cm-label">유형</div>
            <div className="cm-types">
              {COLUMN_TYPES.map((t) => (
                <button
                  key={t.type}
                  className={`cm-type${col.type === t.type ? ' on' : ''}`}
                  onClick={() => changeColumnType(col.key, t.type)}
                >
                  <span className="ti">{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
            {col.type === 'select' && (
              <OptionEditor col={col} updateColumnOptions={updateColumnOptions} />
            )}
            {col.type === 'relation' && (
              <RelationSettings col={col} updateColumnMeta={updateColumnMeta} dbx={dbx} />
            )}
            {col.type === 'formula' && (
              <FormulaSettings col={col} updateColumnMeta={updateColumnMeta} />
            )}
            {col.type === 'rollup' && (
              <RollupSettings col={col} columns={columns} updateColumnMeta={updateColumnMeta} dbx={dbx} />
            )}
            <div className="cm-sep" />
            <button
              className="cm-action danger"
              onClick={() => { deleteColumn(col.key); setOpen(false); }}
            >
              🗑 속성 삭제
            </button>
          </div>
        )}
      </div>
    </th>
  );
}

/* select 옵션 편집기 (메뉴 내부) */
function OptionEditor({ col, updateColumnOptions }) {
  const options = col.options || [];
  const [newOpt, setNewOpt] = useState('');

  const add = () => {
    const name = newOpt.trim();
    if (!name || options.some((o) => o.name === name)) { setNewOpt(''); return; }
    const palette = OPTION_COLORS[options.length % OPTION_COLORS.length].name;
    updateColumnOptions(col.key, [...options, { name, color: palette }]);
    setNewOpt('');
  };
  const rename = (i, name) => {
    const next = options.map((o, idx) => (idx === i ? { ...o, name } : o));
    updateColumnOptions(col.key, next);
  };
  const recolor = (i, color) => {
    const next = options.map((o, idx) => (idx === i ? { ...o, color } : o));
    updateColumnOptions(col.key, next);
  };
  const remove = (i) => {
    updateColumnOptions(col.key, options.filter((_, idx) => idx !== i));
  };

  return (
    <div className="opt-editor">
      <div className="cm-label">옵션</div>
      {options.map((o, i) => (
        <div className="oe-row" key={i}>
          <span className={`tag ${COLOR_CLASS[o.color] || 'tg'} oe-dot`}>●</span>
          <input
            className="oe-name"
            value={o.name}
            onChange={(e) => rename(i, e.target.value)}
          />
          <select value={o.color} onChange={(e) => recolor(i, e.target.value)} className="oe-color">
            {OPTION_COLORS.map((c) => (
              <option key={c.name} value={c.name}>{c.label}</option>
            ))}
          </select>
          <button className="oe-del" onClick={() => remove(i)} title="삭제">×</button>
        </div>
      ))}
      <div className="opt-add">
        <input
          value={newOpt}
          placeholder="옵션 추가…"
          onChange={(e) => setNewOpt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <button onClick={add}>추가</button>
      </div>
    </div>
  );
}

/* relation 설정 — 대상 데이터베이스 선택 (메뉴 내부) */
function RelationSettings({ col, updateColumnMeta, dbx }) {
  const isRoot = col.relationDb && UUID_RE.test(String(col.relationDb));
  return (
    <div className="opt-editor">
      <div className="cm-label">대상 데이터베이스</div>
      <select
        className="cm-meta-select"
        value={isRoot ? col.relationDb : ''}
        onChange={(e) => updateColumnMeta(col.key, { relationDb: e.target.value || null })}
      >
        <option value="">선택…</option>
        {(dbx.databases || []).map((d) => (
          <option key={d.id} value={d.id}>{d.icon ? `${d.icon} ` : ''}{d.title || '제목 없음'}</option>
        ))}
      </select>
      {col.relationDb && !isRoot && (
        <div className="cm-hint">
          가져온 노션 DB 참조입니다. 셀을 편집하려면 위에서 루트 데이터베이스를 선택하세요.
        </div>
      )}
    </div>
  );
}

/* formula 설정 — 수식 입력 + 문법 검사 (메뉴 내부) */
function FormulaSettings({ col, updateColumnMeta }) {
  const [draft, setDraft] = useState(col.formula || '');
  useEffect(() => { setDraft(col.formula || ''); }, [col.key]); // eslint-disable-line

  let err = null;
  if (draft.trim()) {
    try { parseFormula(draft); } catch (e) { err = e.message || String(e); }
  }
  const dirty = draft !== (col.formula || '');

  return (
    <div className="opt-editor">
      <div className="cm-label">수식</div>
      <textarea
        className="cm-formula"
        rows={3}
        value={draft}
        spellCheck={false}
        placeholder={'예: if(prop("상태") == "완료", "✓", "")'}
        onChange={(e) => setDraft(e.target.value)}
      />
      {err && <div className="cm-err">{err}</div>}
      {dirty && (
        <button
          className="cm-action primary"
          disabled={!!err}
          onClick={() => updateColumnMeta(col.key, { formula: draft })}
        >
          수식 저장
        </button>
      )}
      <div className="cm-hint">
        prop("속성명") · 사칙연산 · 비교 · and/or/not · if() · concat() · length() ·
        round() · abs() · empty() · now() · dateBetween(d1, d2, "days") · format()
      </div>
    </div>
  );
}

/* rollup 설정 — 기준 관계 속성 + 대상 속성 + 집계 (메뉴 내부) */
function RollupSettings({ col, columns, updateColumnMeta, dbx }) {
  const cfg = col.rollup || {};
  const relCols = columns.filter((c) => c.type === 'relation');
  const relCol = relCols.find((c) => c.key === cfg.relationKey);
  const dbId = relCol?.relationDb && UUID_RE.test(String(relCol.relationDb)) ? relCol.relationDb : null;

  // 대상 DB 스키마(속성 목록)가 필요하므로 미리 로드
  useEffect(() => {
    if (dbId) dbx.ensureTarget(dbId);
  }, [dbId]); // eslint-disable-line

  const target = dbId ? dbx.targets[dbId] : null;
  const targetCols = target && !target.loading && !target.error ? target.columns : [];

  const save = (patch) =>
    updateColumnMeta(col.key, {
      rollup: {
        relationKey: cfg.relationKey || '',
        targetKey: cfg.targetKey || 'title',
        agg: cfg.agg || 'show',
        ...patch,
      },
    });

  return (
    <div className="opt-editor">
      <div className="cm-label">기준 관계 속성</div>
      {relCols.length === 0 ? (
        <div className="cm-hint">이 데이터베이스에 관계(relation) 속성이 없습니다. 먼저 관계 속성을 추가하세요.</div>
      ) : (
        <select
          className="cm-meta-select"
          value={cfg.relationKey || ''}
          onChange={(e) => save({ relationKey: e.target.value, targetKey: 'title' })}
        >
          <option value="">선택…</option>
          {relCols.map((c) => (
            <option key={c.key} value={c.key}>{c.name}</option>
          ))}
        </select>
      )}
      {relCol && !dbId && (
        <div className="cm-hint">선택한 관계 속성에 대상 데이터베이스가 설정되지 않았습니다.</div>
      )}
      {dbId && (
        <>
          <div className="cm-label">대상 속성</div>
          <select
            className="cm-meta-select"
            value={cfg.targetKey || 'title'}
            onChange={(e) => save({ targetKey: e.target.value })}
          >
            <option value="title">제목</option>
            {targetCols.filter((c) => c.type !== 'title').map((c) => (
              <option key={c.key} value={c.key}>{c.name}</option>
            ))}
          </select>
          <div className="cm-label">집계</div>
          <select
            className="cm-meta-select"
            value={cfg.agg || 'show'}
            onChange={(e) => save({ agg: e.target.value })}
          >
            {ROLLUP_AGGS.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

/* 새 컬럼 추가 버튼 (헤더 맨 끝) */
function AddColumnHeader({ addColumn, columns, dbx }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('text');
  // 타입별 부가 설정
  const [relDb, setRelDb] = useState('');
  const [formula, setFormula] = useState('');
  const [ruRel, setRuRel] = useState('');
  const [ruKey, setRuKey] = useState('title');
  const [ruAgg, setRuAgg] = useState('show');
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const relCols = columns.filter((c) => c.type === 'relation');
  const ruRelCol = relCols.find((c) => c.key === ruRel);
  const ruDbId = ruRelCol?.relationDb && UUID_RE.test(String(ruRelCol.relationDb)) ? ruRelCol.relationDb : null;
  useEffect(() => {
    if (type === 'rollup' && ruDbId) dbx.ensureTarget(ruDbId);
  }, [type, ruDbId]); // eslint-disable-line
  const ruTarget = ruDbId ? dbx.targets[ruDbId] : null;
  const ruTargetCols = ruTarget && !ruTarget.loading && !ruTarget.error ? ruTarget.columns : [];

  let formulaErr = null;
  if (type === 'formula' && formula.trim()) {
    try { parseFormula(formula); } catch (e) { formulaErr = e.message || String(e); }
  }

  const submit = async () => {
    if (type === 'formula' && formulaErr) return;
    const meta = {};
    if (type === 'relation' && relDb) meta.relationDb = relDb;
    if (type === 'formula') meta.formula = formula;
    if (type === 'rollup') meta.rollup = { relationKey: ruRel, targetKey: ruKey || 'title', agg: ruAgg || 'show' };
    await addColumn({ name: name.trim() || '새 속성', type, meta });
    setName('');
    setType('text');
    setRelDb('');
    setFormula('');
    setRuRel('');
    setRuKey('title');
    setRuAgg('show');
    setOpen(false);
  };

  return (
    <th className="th-add">
      <div className="th-inner" ref={ref}>
        <button className="th-add-btn" onClick={() => setOpen((o) => !o)} title="속성 추가">＋</button>
        {open && (
          <div className="col-menu">
            <div className="cm-section">
              <input
                className="cm-name"
                value={name}
                placeholder="속성 이름"
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              />
            </div>
            <div className="cm-label">유형</div>
            <div className="cm-types">
              {COLUMN_TYPES.map((t) => (
                <button
                  key={t.type}
                  className={`cm-type${type === t.type ? ' on' : ''}`}
                  onClick={() => setType(t.type)}
                >
                  <span className="ti">{t.icon}</span>{t.label}
                </button>
              ))}
            </div>
            {type === 'relation' && (
              <div className="opt-editor">
                <div className="cm-label">대상 데이터베이스</div>
                <select className="cm-meta-select" value={relDb} onChange={(e) => setRelDb(e.target.value)}>
                  <option value="">선택…</option>
                  {(dbx.databases || []).map((d) => (
                    <option key={d.id} value={d.id}>{d.icon ? `${d.icon} ` : ''}{d.title || '제목 없음'}</option>
                  ))}
                </select>
              </div>
            )}
            {type === 'formula' && (
              <div className="opt-editor">
                <div className="cm-label">수식</div>
                <textarea
                  className="cm-formula"
                  rows={3}
                  value={formula}
                  spellCheck={false}
                  placeholder={'예: prop("가격") * prop("수량")'}
                  onChange={(e) => setFormula(e.target.value)}
                />
                {formulaErr && <div className="cm-err">{formulaErr}</div>}
              </div>
            )}
            {type === 'rollup' && (
              <div className="opt-editor">
                <div className="cm-label">기준 관계 속성</div>
                {relCols.length === 0 ? (
                  <div className="cm-hint">관계(relation) 속성을 먼저 추가하세요. 추가 후 속성 메뉴에서 설정할 수 있습니다.</div>
                ) : (
                  <select className="cm-meta-select" value={ruRel} onChange={(e) => { setRuRel(e.target.value); setRuKey('title'); }}>
                    <option value="">선택…</option>
                    {relCols.map((c) => (
                      <option key={c.key} value={c.key}>{c.name}</option>
                    ))}
                  </select>
                )}
                {ruDbId && (
                  <>
                    <div className="cm-label">대상 속성</div>
                    <select className="cm-meta-select" value={ruKey} onChange={(e) => setRuKey(e.target.value)}>
                      <option value="title">제목</option>
                      {ruTargetCols.filter((c) => c.type !== 'title').map((c) => (
                        <option key={c.key} value={c.key}>{c.name}</option>
                      ))}
                    </select>
                    <div className="cm-label">집계</div>
                    <select className="cm-meta-select" value={ruAgg} onChange={(e) => setRuAgg(e.target.value)}>
                      {ROLLUP_AGGS.map((a) => (
                        <option key={a.id} value={a.id}>{a.label}</option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}
            <div className="cm-sep" />
            <button className="cm-action primary" onClick={submit} disabled={type === 'formula' && !!formulaErr}>＋ 추가</button>
          </div>
        )}
      </div>
    </th>
  );
}

function iconFor(type) {
  switch (type) {
    case 'title': return 'Aa';
    case 'select': return '◉';
    case 'person': return '👤';
    case 'date': return '📅';
    case 'checkbox': return '☑';
    case 'number': return '#';
    case 'url': return '🔗';
    case 'relation': return '↗';
    case 'formula': return 'ƒ';
    case 'rollup': return 'Σ';
    default: return 'Aa';
  }
}

/* ====================== 표 뷰 ====================== */
function TableView({ columns, rows, dbx, updateCell, createRow, addColumn, renameColumn, changeColumnType, deleteColumn, updateColumnOptions, updateColumnMeta }) {
  return (
    <div>
      <table>
        <thead>
          <tr>
            {columns.map((c) => (
              <ColumnHeader
                key={c.key}
                col={c}
                columns={columns}
                renameColumn={renameColumn}
                changeColumnType={changeColumnType}
                deleteColumn={deleteColumn}
                updateColumnOptions={updateColumnOptions}
                updateColumnMeta={updateColumnMeta}
                dbx={dbx}
              />
            ))}
            <AddColumnHeader addColumn={addColumn} columns={columns} dbx={dbx} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.page_id}>
              {columns.map((c) => (
                <td key={c.key}>
                  <Cell
                    col={c}
                    row={row}
                    columns={columns}
                    dbx={dbx}
                    onChange={(k, v) => updateCell(row.page_id, k, v)}
                    onUpdateColumnOptions={updateColumnOptions}
                  />
                </td>
              ))}
              <td className="td-pad" />
            </tr>
          ))}
        </tbody>
      </table>
      <button className="newrow" onClick={() => createRow()}>＋ 새로 만들기</button>
    </div>
  );
}

/* 보드/캘린더 뷰 — ./BoardView.jsx, ./CalendarView.jsx 로 추출 (2026-07-06, 임베드 공유) */

/* ====================== 차트(chart) 뷰 ====================== */
function ChartView({ columns, rows, view, onChangeConfig }) {
  // 그룹 가능 컬럼: title/계산형(formula·rollup — properties 에 값이 없음) 제외
  const groupCols = columns.filter((c) => c.type !== 'title' && !COMPUTED_TYPES.includes(c.type));
  // 합계 대상 number 컬럼
  const numberCols = columns.filter((c) => c.type === 'number');

  const cfg = view.config || {};
  const chartType = cfg.chartType || 'bar';
  const groupBy = cfg.groupBy || groupCols[0]?.key || '';
  // measure: 'count' | { sum: <numberColumnKey> }
  const measure = cfg.measure || 'count';
  const measureIsSum = measure && typeof measure === 'object' && measure.sum;
  const sumKey = measureIsSum ? measure.sum : '';

  const groupCol = columns.find((c) => c.key === groupBy);

  const save = (patch) => onChangeConfig({ chartType, groupBy, measure, ...patch });

  // 그룹 값 표시 문자열 (checkbox 등 보정)
  const labelOf = (raw, col) => {
    if (raw === true || raw === 'true') return '예';
    if (col?.type === 'checkbox' && (raw === false || raw === 'false')) return '아니오';
    if (raw === '' || raw == null) return '미지정';
    return String(raw);
  };

  // 집계 데이터 만들기
  const data = useMemo(() => {
    if (!groupBy) return [];
    const isSelect = groupCol?.type === 'select';
    const buckets = new Map(); // name → { name, value, color }

    // select 면 옵션 순서를 먼저 깔아 빈 그룹도 0으로 노출
    if (isSelect) {
      (groupCol.options || []).forEach((o) => {
        buckets.set(o.name, { name: o.name, value: 0, color: COLOR_HEX[o.color] || null });
      });
    }

    rows.forEach((r) => {
      const raw = groupBy === 'title' ? r.title : r.properties?.[groupBy];
      const name = labelOf(raw, groupCol);
      if (!buckets.has(name)) {
        buckets.set(name, { name, value: 0, color: null });
      }
      const b = buckets.get(name);
      if (measureIsSum) {
        const n = Number(r.properties?.[sumKey]);
        if (!Number.isNaN(n)) b.value += n;
      } else {
        b.value += 1;
      }
    });

    let arr = Array.from(buckets.values());
    // 색 배정 (옵션 색 없으면 팔레트 순환)
    arr = arr.map((d, i) => ({ ...d, color: d.color || CHART_PALETTE[i % CHART_PALETTE.length] }));
    // count 모드에서 값 0 인 '미지정' 만 있는 경우는 그대로 둠 (안내는 합계 0 으로 처리)
    return arr;
  }, [rows, groupBy, groupCol, measureIsSum, sumKey]);

  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const measureLabel = measureIsSum
    ? `${columns.find((c) => c.key === sumKey)?.name || '숫자'} 합계`
    : '개수';

  return (
    <div className="chart-view">
      <div className="chart-bar">
        {/* 차트 종류 */}
        <div className="cb-group">
          <span className="cb-label">차트</span>
          <div className="cb-seg">
            {[
              { t: 'bar', label: '막대', icon: '▥' },
              { t: 'line', label: '선', icon: '📈' },
              { t: 'pie', label: '원', icon: '◔' },
            ].map((o) => (
              <button
                key={o.t}
                className={`cb-seg-btn${chartType === o.t ? ' on' : ''}`}
                onClick={() => save({ chartType: o.t })}
              >
                <span className="ti">{o.icon}</span>{o.label}
              </button>
            ))}
          </div>
        </div>

        {/* 그룹 기준 */}
        <div className="cb-group">
          <span className="cb-label">그룹 기준</span>
          {groupCols.length ? (
            <select
              className="cb-select"
              value={groupBy}
              onChange={(e) => save({ groupBy: e.target.value })}
            >
              {groupCols.map((c) => (
                <option key={c.key} value={c.key}>{c.name}</option>
              ))}
            </select>
          ) : (
            <span className="cb-hint">그룹으로 쓸 컬럼이 없습니다.</span>
          )}
        </div>

        {/* 집계 */}
        <div className="cb-group">
          <span className="cb-label">집계</span>
          <select
            className="cb-select"
            value={measureIsSum ? `sum:${sumKey}` : 'count'}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'count') save({ measure: 'count' });
              else save({ measure: { sum: v.slice(4) } });
            }}
          >
            <option value="count">개수</option>
            {numberCols.map((c) => (
              <option key={c.key} value={`sum:${c.key}`}>{c.name} 합계</option>
            ))}
          </select>
        </div>
      </div>

      {!groupBy ? (
        <div className="db-state">
          <div className="big">📊</div>
          <div>차트 그룹으로 사용할 컬럼을 만들어 주세요.</div>
        </div>
      ) : rows.length === 0 || total === 0 ? (
        <div className="db-state">
          <div className="big">📊</div>
          <div>표시할 데이터가 없습니다.</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {rows.length === 0 ? '행을 추가하면 차트가 그려집니다.' : `선택한 집계(${measureLabel}) 값이 모두 0입니다.`}
          </div>
        </div>
      ) : (
        <div className="chart-box">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'pie' ? (
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="72%"
                  innerRadius="0%"
                  paddingAngle={1}
                  label={(e) => `${e.name} (${e.value})`}
                  labelLine={false}
                >
                  {data.map((d) => (
                    <RechartsCell key={d.name} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [v, measureLabel]} />
              </PieChart>
            ) : chartType === 'line' ? (
              <LineChart data={data} margin={{ top: 12, right: 20, bottom: 8, left: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#787774' }} tickLine={false} axisLine={{ stroke: '#e3e2e0' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#787774' }} tickLine={false} axisLine={{ stroke: '#e3e2e0' }} />
                <Tooltip formatter={(v) => [v, measureLabel]} />
                <Line type="monotone" dataKey="value" stroke="#2383e2" strokeWidth={2} dot={{ r: 3, fill: '#2383e2' }} />
              </LineChart>
            ) : (
              <BarChart data={data} margin={{ top: 12, right: 20, bottom: 8, left: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#787774' }} tickLine={false} axisLine={{ stroke: '#e3e2e0' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#787774' }} tickLine={false} axisLine={{ stroke: '#e3e2e0' }} />
                <Tooltip formatter={(v) => [v, measureLabel]} cursor={{ fill: 'rgba(55,53,47,.04)' }} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {data.map((d) => (
                    <RechartsCell key={d.name} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
          <div className="chart-foot">
            <span className="cf-total">합계 {total}</span>
            <span className="cf-sub">· {measureLabel} 기준 · {data.length}개 그룹</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== 자동화 패널 (우측 드로어) ====================== */
function AutomationPanel({ columns, automations, saveAutomations, onClose }) {
  // 트리거로 쓸 수 있는 컬럼: select(상태) 타입
  const triggerCols = columns.filter((c) => c.type === 'select');
  // 액션으로 쓸 수 있는 컬럼: title/계산형(formula·rollup) 제외
  const actionCols = columns.filter((c) => c.type !== 'title' && !COMPUTED_TYPES.includes(c.type));

  // 새 규칙 입력 상태
  const [whenCol, setWhenCol] = useState(triggerCols[0]?.key || '');
  const [whenEq, setWhenEq] = useState('');
  const [thenCol, setThenCol] = useState('');
  const [thenSet, setThenSet] = useState('');
  const [useToday, setUseToday] = useState(false);

  const whenColumn = columns.find((c) => c.key === whenCol);
  const thenColumn = columns.find((c) => c.key === thenCol);
  const thenIsDate = thenColumn?.type === 'date';

  // 트리거 컬럼 바뀌면 첫 옵션으로 같아질 값 초기화
  useEffect(() => {
    const opts = whenColumn?.options || [];
    setWhenEq((prev) => (opts.some((o) => o.name === prev) ? prev : (opts[0]?.name || '')));
  }, [whenCol]); // eslint-disable-line

  const colName = (key) => columns.find((c) => c.key === key)?.name || key;

  const addRule = async () => {
    if (!whenCol || whenEq === '' || !thenCol) return;
    const setVal = thenIsDate && useToday ? '@today' : thenSet;
    const rule = {
      id: `auto_${Date.now()}`,
      when: { column: whenCol, equals: whenEq },
      then: { column: thenCol, set: setVal },
    };
    await saveAutomations([...(automations || []), rule]);
    // 입력 초기화(트리거는 유지)
    setThenCol('');
    setThenSet('');
    setUseToday(false);
  };

  const removeRule = async (id) => {
    await saveAutomations((automations || []).filter((r) => r.id !== id));
  };

  const describeSet = (then) => {
    if (then.set === '@today') return '오늘 날짜';
    if (then.set === '' || then.set == null) return '비우기';
    return `"${then.set}"`;
  };

  return (
    <>
      <div className="auto-overlay" onClick={onClose} />
      <div className="auto-drawer" role="dialog" aria-label="자동화">
        <div className="auto-head">
          <span className="auto-title">⚡ 자동화</span>
          <button className="auto-x" onClick={onClose} title="닫기">×</button>
        </div>
        <div className="auto-desc">
          어떤 속성이 특정 값이 되면 다른 속성을 자동으로 설정합니다.
        </div>

        {/* 규칙 목록 */}
        <div className="auto-list">
          {(automations || []).length === 0 && (
            <div className="auto-empty">아직 규칙이 없습니다. 아래에서 추가하세요.</div>
          )}
          {(automations || []).map((r) => (
            <div className="auto-rule" key={r.id}>
              <div className="auto-rule-text">
                <b>{colName(r.when.column)}</b> 이(가){' '}
                <span className="auto-chip">{r.when.equals || '비어 있음'}</span> 이 되면{' '}
                → <b>{colName(r.then.column)}</b> 을(를){' '}
                <span className="auto-chip">{describeSet(r.then)}</span> (으)로 설정
              </div>
              <button className="auto-del" onClick={() => removeRule(r.id)} title="삭제">×</button>
            </div>
          ))}
        </div>

        {/* 새 규칙 추가 */}
        <div className="auto-form">
          <div className="auto-form-title">＋ 규칙 추가</div>

          {triggerCols.length === 0 ? (
            <div className="auto-hint">
              트리거로 쓸 선택(select) 컬럼이 없습니다. 표 뷰에서 선택 컬럼을 추가하세요.
            </div>
          ) : (
            <>
              <label className="auto-field">
                <span className="auto-flabel">조건(When) — 컬럼</span>
                <select value={whenCol} onChange={(e) => setWhenCol(e.target.value)}>
                  {triggerCols.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
              </label>

              <label className="auto-field">
                <span className="auto-flabel">같아질 값</span>
                <select value={whenEq} onChange={(e) => setWhenEq(e.target.value)}>
                  {(whenColumn?.options || []).map((o) => (
                    <option key={o.name} value={o.name}>{o.name}</option>
                  ))}
                </select>
              </label>

              <label className="auto-field">
                <span className="auto-flabel">동작(Then) — 컬럼</span>
                <select value={thenCol} onChange={(e) => { setThenCol(e.target.value); setUseToday(false); }}>
                  <option value="">컬럼 선택…</option>
                  {actionCols.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
              </label>

              {thenCol && (
                <div className="auto-field">
                  <span className="auto-flabel">설정값</span>
                  {thenIsDate && (
                    <label className="auto-today">
                      <input
                        type="checkbox"
                        checked={useToday}
                        onChange={(e) => setUseToday(e.target.checked)}
                      />
                      오늘(@today)로 설정
                    </label>
                  )}
                  {!(thenIsDate && useToday) && (
                    thenColumn?.type === 'select' ? (
                      <select value={thenSet} onChange={(e) => setThenSet(e.target.value)}>
                        <option value="">비우기</option>
                        {(thenColumn.options || []).map((o) => (
                          <option key={o.name} value={o.name}>{o.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={thenIsDate ? 'date' : 'text'}
                        value={thenSet}
                        placeholder="설정할 값"
                        onChange={(e) => setThenSet(e.target.value)}
                      />
                    )
                  )}
                </div>
              )}

              <button
                className="auto-add-btn"
                onClick={addRule}
                disabled={!whenCol || whenEq === '' || !thenCol}
              >
                규칙 추가
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ====================== 폼(form) 빌더 ====================== */
function FormBuilder({ view, columns }) {
  // 폼 설정: 노출 컬럼(visible) / 제목 / 설명 / 게시 여부 / 토큰
  // config.form 에 빌더 설정(제목/설명/노출 컬럼)을 로컬로 유지하되,
  // 게시 상태(token/published)는 서버 RPC(get_form_publish/set_form_publish)가 source of truth.
  const titleCol = columns.find((c) => c.type === 'title');
  // 계산형(formula·rollup)은 제출값이 없으므로 폼 노출 후보에서 제외
  const selectable = columns.filter((c) => !COMPUTED_TYPES.includes(c.type));

  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  // visible: { [key]: true } — 미설정이면 기본 전체 노출
  const [visible, setVisible] = useState(() => {
    const init = {};
    columns.forEach((c) => { init[c.key] = true; });
    return init;
  });
  const [published, setPublished] = useState(false);
  const [token, setToken] = useState('');
  const [loadingState, setLoadingState] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  // 진입 시 현재 게시 상태 로드
  useEffect(() => {
    let active = true;
    setLoadingState(true);
    setErr('');
    (async () => {
      const { data, error } = await supabase.rpc('get_form_publish', { p_view: view.id });
      if (!active) return;
      if (error) {
        setErr(error.message);
      } else if (data) {
        setPublished(!!data.published);
        setToken(data.token || '');
        if (data.title != null) setTitle(data.title);
        if (data.description != null) setDesc(data.description);
        if (Array.isArray(data.fields) && data.fields.length) {
          const v = {};
          columns.forEach((c) => { v[c.key] = data.fields.includes(c.key); });
          setVisible(v);
        }
      }
      setLoadingState(false);
    })();
    return () => { active = false; };
  }, [view.id]);

  // 현재 노출 컬럼 키 목록
  const visKeys = (vis) => selectable.filter((c) => vis[c.key]).map((c) => c.key);

  // 게시된 상태에서 노출 컬럼/제목/설명을 서버에 반영
  const persistForm = async (nextVisible) => {
    if (!published) return;
    await supabase.rpc('set_form_publish', {
      p_view: view.id,
      p_publish: true,
      p_title: title || '폼',
      p_desc: desc || '',
      p_fields: visKeys(nextVisible),
    });
  };

  const toggleVisible = (key) => {
    // setState updater 안에서 부수효과(RPC)를 호출하면 StrictMode 이중 실행 등으로
    // 중복 호출될 수 있으므로 next 를 밖에서 계산해 한 번만 저장한다.
    const next = { ...visible, [key]: !visible[key] };
    setVisible(next);
    persistForm(next);
  };

  const publicUrl = token ? `${window.location.origin}/form/${token}` : '';

  // 게시 ON: set_form_publish(p_publish=true) → token 수신
  const publish = async () => {
    setBusy(true);
    setErr('');
    const { data, error } = await supabase.rpc('set_form_publish', {
      p_view: view.id,
      p_publish: true,
      p_title: title || (titleCol?.name ? `${titleCol.name} 폼` : '폼'),
      p_desc: desc || '',
      p_fields: visKeys(visible),
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    if (data) {
      setPublished(!!data.published);
      setToken(data.token || '');
    }
  };

  // 게시 OFF
  const unpublish = async () => {
    setBusy(true);
    setErr('');
    const { data, error } = await supabase.rpc('set_form_publish', {
      p_view: view.id,
      p_publish: false,
      p_title: title || '',
      p_desc: desc || '',
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    if (data) setPublished(!!data.published);
  };

  // 제목/설명 변경 후 재게시(이미 게시된 상태면 갱신)
  const saveMeta = async () => {
    if (!published) return;
    await supabase.rpc('set_form_publish', {
      p_view: view.id,
      p_publish: true,
      p_title: title || '폼',
      p_desc: desc || '',
      p_fields: visKeys(visible),
    });
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      setErr('클립보드 복사에 실패했습니다. 링크를 직접 복사하세요.');
    }
  };

  const visibleCols = selectable.filter((c) => visible[c.key]);

  return (
    <div className="form-builder">
      <div className="fb-grid">
        {/* 왼쪽: 빌더 설정 */}
        <div className="fb-edit">
          <div className="fb-card">
            <div className="fb-card-title">폼 정보</div>
            <label className="fb-field">
              <span className="fb-flabel">제목</span>
              <input
                className="fb-input"
                value={title}
                placeholder="폼 제목"
                onChange={(e) => setTitle(e.target.value)}
                onBlur={saveMeta}
              />
            </label>
            <label className="fb-field">
              <span className="fb-flabel">설명</span>
              <textarea
                className="fb-input fb-textarea"
                value={desc}
                placeholder="폼 설명 (선택)"
                rows={2}
                onChange={(e) => setDesc(e.target.value)}
                onBlur={saveMeta}
              />
            </label>
          </div>

          <div className="fb-card">
            <div className="fb-card-title">질문 (컬럼)</div>
            <div className="fb-hint">폼에 노출할 컬럼을 선택하세요. 순서는 컬럼 순서를 따릅니다.</div>
            <div className="fb-qlist">
              {selectable.map((c) => (
                <label className="fb-qrow" key={c.key}>
                  <input
                    type="checkbox"
                    checked={!!visible[c.key]}
                    onChange={() => toggleVisible(c.key)}
                  />
                  <span className="ti">{iconFor(c.type)}</span>
                  <span className="fb-qname">{c.name}</span>
                  <span className="fb-qtype">{(COLUMN_TYPES.find((t) => t.type === c.type) || {}).label || (c.type === 'title' ? '제목' : c.type)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="fb-card fb-publish">
            <div className="fb-pub-head">
              <div>
                <div className="fb-card-title" style={{ margin: 0 }}>게시</div>
                <div className="fb-hint" style={{ margin: '4px 0 0' }}>
                  {published ? '이 폼은 외부에 공개되어 있습니다.' : '게시하면 로그인 없이 누구나 제출할 수 있는 링크가 생성됩니다.'}
                </div>
              </div>
              <button
                className={`fb-toggle${published ? ' on' : ''}`}
                disabled={busy || loadingState}
                onClick={() => (published ? unpublish() : publish())}
                role="switch"
                aria-checked={published}
                title={published ? '게시 해제' : '게시'}
              >
                <span className="fb-knob" />
              </button>
            </div>

            {published && token && (
              <div className="fb-link-box">
                <input className="fb-link" value={publicUrl} readOnly onFocus={(e) => e.target.select()} />
                <button className="fb-copy" onClick={copyLink}>{copied ? '복사됨 ✓' : '복사'}</button>
                <a className="fb-open" href={publicUrl} target="_blank" rel="noreferrer">열기 ↗</a>
              </div>
            )}

            {err && <div className="fb-err">{err}</div>}
          </div>
        </div>

        {/* 오른쪽: 미리보기 */}
        <div className="fb-preview">
          <div className="fb-pv-card">
            <div className="fb-pv-title">{title || '폼 제목'}</div>
            {desc && <div className="fb-pv-desc">{desc}</div>}
            {visibleCols.length === 0 ? (
              <div className="fb-pv-empty">노출할 질문을 선택하세요.</div>
            ) : (
              visibleCols.map((c) => (
                <div className="fb-pv-q" key={c.key}>
                  <div className="fb-pv-q-label">{c.name}</div>
                  <PreviewField col={c} />
                </div>
              ))
            )}
            <button className="fb-pv-submit" disabled>제출</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* 폼 미리보기용 입력 필드 (비활성) */
function PreviewField({ col }) {
  if (col.type === 'select') {
    return (
      <select className="fb-pv-field" disabled>
        <option>선택…</option>
        {(col.options || []).map((o) => (
          <option key={o.name}>{o.name}</option>
        ))}
      </select>
    );
  }
  if (col.type === 'date') return <input className="fb-pv-field" type="date" disabled />;
  if (col.type === 'checkbox') return <input type="checkbox" disabled />;
  if (col.type === 'number') return <input className="fb-pv-field" type="number" placeholder="숫자" disabled />;
  return <input className="fb-pv-field" placeholder={col.type === 'title' ? '제목' : '입력…'} disabled />;
}

/* ====================== 메인 ====================== */
export default function DatabasePage() {
  const { pageId } = useParams();
  const {
    loading, error, page, columns, views, rows, automations,
    createRow, updateCell,
    addColumn, renameColumn, changeColumnType, deleteColumn, updateColumnOptions, updateColumnMeta,
    saveAutomations, createView, saveViewConfig,
  } = useDatabase(pageId);
  // relation/person/rollup 부가 데이터 (멤버, 대상 DB 캐시, 행 제목, DB 목록)
  const dbx = useRelationContext(page, columns, rows);
  const [activeViewId, setActiveViewId] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [addViewOpen, setAddViewOpen] = useState(false);
  const addViewRef = useRef(null);
  useOutsideClose(addViewRef, () => setAddViewOpen(false));
  // 보드 그룹 / 캘린더 날짜 기준은 클라이언트 로컬 오버라이드로 관리 (view.config 미변경)
  const [viewOverrides, setViewOverrides] = useState({}); // { [viewId]: { groupBy, dateKey } }

  // 데이터베이스 제목 편집 (pages.title) — 자동 저장(디바운스)
  const [title, setTitle] = useState('');
  const titleTimer = useRef(null);
  useEffect(() => { setTitle(page?.title || ''); }, [page?.id]); // eslint-disable-line
  const onTitleChange = (e) => {
    const v = e.target.value;
    setTitle(v);
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(async () => {
      await supabase.schema('workspace').from('pages').update({ title: v }).eq('id', pageId);
      window.dispatchEvent(new Event('rt:pages'));
    }, 500);
  };

  const activeView = useMemo(() => {
    if (!views.length) return null;
    return views.find((v) => v.id === activeViewId) || views[0];
  }, [views, activeViewId]);

  // 로컬 오버라이드를 적용한 뷰
  const effectiveView = useMemo(() => {
    if (!activeView) return null;
    const ov = viewOverrides[activeView.id];
    if (!ov) return activeView;
    return { ...activeView, config: { ...(activeView.config || {}), ...ov } };
  }, [activeView, viewOverrides]);

  const setOverride = (patch) => {
    if (!activeView) return;
    setViewOverrides((prev) => ({
      ...prev,
      [activeView.id]: { ...(prev[activeView.id] || {}), ...patch },
    }));
  };

  /* ---------- 필터 / 정렬 / 검색 (모든 뷰 공통 단일 파이프라인) ----------
     - 필터·정렬 상태는 db_views.config(jsonb) 에 persist (saveViewConfig)
     - 검색어는 세션 로컬 상태 (persist 안 함) */
  const [searchText, setSearchText] = useState('');
  const filters = useMemo(
    () => (Array.isArray(effectiveView?.config?.filters) ? effectiveView.config.filters : []),
    [effectiveView]
  );
  const sorts = useMemo(
    () => (Array.isArray(effectiveView?.config?.sorts) ? effectiveView.config.sorts : []),
    [effectiveView]
  );

  // DB 에 저장된 config(activeView.config) 기준으로 patch 병합 저장.
  // (viewOverrides 의 로컬 groupBy/dateKey 오버라이드는 DB 에 쓰지 않는다)
  const persistViewConfig = (patch) => {
    if (!activeView) return;
    saveViewConfig(activeView.id, { ...(activeView.config || {}), ...patch });
  };

  // 검색/필터/정렬용 값 접근자 — formula/rollup 계산값, person 이름, relation 제목 기준
  const getValue = useMemo(() => makeGetValue(columns, dbx), [columns, dbx]);

  const visibleRows = useMemo(
    () => applyPipeline(rows, columns, { filters, sorts, search: searchText, getValue }),
    [rows, columns, filters, sorts, searchText, getValue]
  );

  if (loading) {
    return (
      <div className="db-page db-state">
        <div className="spinner" />
        <div>데이터베이스 불러오는 중…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="db-page db-state">
        <div className="big">⚠️</div>
        <div>데이터베이스를 불러오지 못했습니다.</div>
        <div className="muted" style={{ fontSize: 12 }}>{error}</div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="db-page db-state">
        <div className="big">🗂️</div>
        <div>데이터베이스 페이지를 찾을 수 없습니다.</div>
      </div>
    );
  }

  return (
    <div className="db-page">
      <div className="db">
        <h1>
          <span className="db-emoji">{page.icon || '🗂️'}</span>
          <input
            value={title}
            onChange={onTitleChange}
            placeholder="제목 없는 데이터베이스"
            spellCheck={false}
            aria-label="데이터베이스 제목"
            style={{ border: 'none', outline: 'none', background: 'transparent', font: 'inherit', color: 'inherit', flex: 1, minWidth: 0, padding: 0 }}
          />
        </h1>
        <div className="desc">같은 데이터를 표·보드·캘린더로 전환합니다. 각 행(row)은 그 자체로 페이지입니다.</div>

        <div className="vtabs">
          {views.map((v) => (
            <button
              key={v.id}
              className={`vt${activeView && v.id === activeView.id ? ' on' : ''}`}
              onClick={() => setActiveViewId(v.id)}
            >
              {/* 뷰 이름 우선 — 같은 타입 뷰 여러 개(예: 상태별 표 3개)를 구분 (DatabaseEmbed 와 동일 규칙) */}
              {v.name || VIEW_LABEL[v.view_type] || v.view_type}
            </button>
          ))}
          <div className="vt-add-wrap" ref={addViewRef}>
            <button className="vt add" onClick={() => setAddViewOpen((o) => !o)}>＋ 뷰 추가</button>
            {addViewOpen && (
              <div className="vt-add-menu">
                {[
                  { type: 'table', label: '표', icon: '▦' },
                  { type: 'board', label: '보드', icon: '▥' },
                  { type: 'calendar', label: '캘린더', icon: '📅' },
                  { type: 'gallery', label: '갤러리', icon: '🖼' },
                  { type: 'list', label: '리스트', icon: '☰' },
                  { type: 'timeline', label: '타임라인', icon: '📆' },
                  { type: 'chart', label: '차트', icon: '📊' },
                  { type: 'form', label: '폼', icon: '📝' },
                ].map((vt) => (
                  <button
                    key={vt.type}
                    className="vt-add-item"
                    onClick={async () => {
                      setAddViewOpen(false);
                      const created = await createView({ view_type: vt.type, name: vt.label });
                      if (created) setActiveViewId(created.id);
                      else {
                        // createView 는 실패 시 null (전역 error 로 올리지 않음) — 여기서만 안내
                        window.alert(
                          `'${vt.label}' 뷰를 추가하지 못했습니다.\n`
                          + '리스트·타임라인 뷰는 DB 마이그레이션 0025 적용이 필요할 수 있습니다.'
                        );
                      }
                    }}
                  >
                    <span className="ti">{vt.icon}</span>{vt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {effectiveView && effectiveView.view_type !== 'form' && (
        <div className="dbtools">
          <FilterControl
            columns={columns}
            filters={filters}
            onChange={(next) => persistViewConfig({ filters: next })}
          />
          <SortControl
            columns={columns}
            sorts={sorts}
            onChange={(next) => persistViewConfig({ sorts: next })}
          />
          <SearchControl value={searchText} onChange={setSearchText} />
          <button className="t auto-btn" onClick={() => setAutoOpen(true)} title="자동화 규칙">
            ⚡ 자동화{automations.length ? ` (${automations.length})` : ''}
          </button>
          <button
            className="t btn-quick"
            onClick={() => {
              // 미리 정한 속성으로 새 행 추가: 첫 번째 select 컬럼을 '대기'(없으면 첫 옵션)로
              const sel = columns.find((c) => c.type === 'select');
              const init = {};
              if (sel) {
                const opts = sel.options || [];
                const wait = opts.find((o) => o.name === '대기');
                if (wait) init[sel.key] = '대기';
                else if (opts[0]) init[sel.key] = opts[0].name;
              }
              createRow(init);
            }}
            title="대기 상태로 새 행 추가"
          >
            🔘 버튼
          </button>
          <button className="new" onClick={() => createRow()}>＋ 새로 만들기</button>
        </div>
        )}

        {effectiveView?.view_type === 'form' && (
          <FormBuilder view={effectiveView} columns={columns} />
        )}
        {effectiveView?.view_type === 'table' && (
          <TableView
            columns={columns}
            rows={visibleRows}
            dbx={dbx}
            updateCell={updateCell}
            createRow={createRow}
            addColumn={addColumn}
            renameColumn={renameColumn}
            changeColumnType={changeColumnType}
            deleteColumn={deleteColumn}
            updateColumnOptions={updateColumnOptions}
            updateColumnMeta={updateColumnMeta}
          />
        )}
        {effectiveView?.view_type === 'board' && (
          <BoardView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            peopleById={dbx.peopleById}
            updateCell={updateCell}
            createRow={createRow}
            onChangeGroupBy={(key) => setOverride({ groupBy: key })}
          />
        )}
        {effectiveView?.view_type === 'calendar' && (
          <CalendarView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            onChangeDateKey={(key) => setOverride({ dateKey: key })}
          />
        )}
        {effectiveView?.view_type === 'gallery' && (
          <GalleryView columns={columns} rows={visibleRows} peopleById={dbx.peopleById} createRow={createRow} />
        )}
        {effectiveView?.view_type === 'list' && (
          <ListView columns={columns} rows={visibleRows} peopleById={dbx.peopleById} createRow={createRow} />
        )}
        {effectiveView?.view_type === 'timeline' && (
          <TimelineView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            onChangeDateKey={(key) => setOverride({ dateKey: key })}
          />
        )}
        {effectiveView?.view_type === 'chart' && (
          <ChartView
            columns={columns}
            rows={visibleRows}
            view={effectiveView}
            onChangeConfig={(config) => persistViewConfig(config)}
          />
        )}
        {effectiveView && !['table', 'board', 'calendar', 'gallery', 'list', 'timeline', 'chart', 'form'].includes(effectiveView.view_type) && (
          <div className="db-state">
            <div className="big">🚧</div>
            <div>{VIEW_LABEL[effectiveView.view_type] || effectiveView.view_type} 뷰는 아직 준비 중입니다.</div>
          </div>
        )}
      </div>

      {autoOpen && (
        <AutomationPanel
          columns={columns}
          automations={automations}
          saveAutomations={saveAutomations}
          onClose={() => setAutoOpen(false)}
        />
      )}
    </div>
  );
}
